import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import classNames from 'classnames';
import { Paragraph, SceneTemplate } from '@/model';
import { toSpliced, withArray } from '@/util/arrayUtil';
import { getNewTextItem } from '@/util/helper/paragraphHelper';
import { useScenarioId } from '@/hook';
import Tooltip from '@/component/Tooltip';
import Button from '@/component/Button';
import { SortableItemWrapper, SortableWrapper } from '@/component/SortableWrapper';
import ChevronArrowIcon from '@/component/icon/ChevronArrowIcon';

import { EditorParagraph } from './model';
import ParagraphCard from './ParagraphCard';
import TemplateConvertModal from './TemplateConvertModal';


interface ParagraphEditorProps {
    id: number;
    className?: string;
    defaultParagraphs?: Paragraph[];
    scrollToBottom: () => void;
    onChange: (paragraphs: Paragraph[]) => void;
}

const ParagraphEditor: React.FC<ParagraphEditorProps> = (props) => {
    const { id: holderId, className: propsClassName, defaultParagraphs, scrollToBottom, onChange: propsOnChange } = props;

    const intl = useIntl();
    const scenarioId = useScenarioId();

    const [paragraphs, setParagraphs] = useState<EditorParagraph[]>(defaultParagraphs?.map(p => ({ ...p, isOpen: true })) ?? []);

    const update = (paragraphs: EditorParagraph[]) => {
        setParagraphs(paragraphs);
        propsOnChange(paragraphs.map(paragraph => {
            const { isOpen, ...rest } = paragraph;
            return rest;
        }));
    };

    const onToggleOpen = (index: number) => {
        setParagraphs(prev => {
            const target = prev[index];
            return withArray(prev, index, { ...target, isOpen: !target.isOpen });
        });
    };

    const onToggleAllOpen = () => {
        setParagraphs(prev => prev.map(p => ({ ...p, isOpen: isAllClosed })));
    };

    const onChange = (index: number, paragraph: EditorParagraph) => {
        update(withArray(paragraphs, index, paragraph));
    };

    const onMove = (index: number, toIndex: number) => {
        const paragraph = { ...paragraphs[index] };
        const removed = toSpliced(paragraphs, index, 1);
        update(toSpliced(removed, toIndex, 0, paragraph));
    };

    const onDuplicate = (p: EditorParagraph) => {
        const lastParagraphId = Math.max(...paragraphs.map(p => p.id || 0), 0);

        update([
            ...paragraphs,
            {
                ...structuredClone(p),
                id: lastParagraphId + 1,
                isOpen: true
            }
        ]);
        scrollToBottom();
    };

    const onConvertToTemplate = (pId: number, template: SceneTemplate) => {
        update(paragraphs.map(p => {
            if (p.id !== pId) {
                return p;
            }
            return {
                ...p,
                type: 'template',
                data_type: 'template',
                data: {
                    template_name: template.name,
                    template_id: template.id
                }
            };
        }));
    };

    const onClickAddParagraph = () => {
        const lastParagraphId = Math.max(...paragraphs.map(p => p.id || 0), 0);

        update([
            ...paragraphs,
            {
                id: lastParagraphId + 1,
                scene_id: holderId,
                scenario_id: scenarioId,
                type: "output",
                data_type: "text",
                data: [getNewTextItem()],
                isOpen: true
            }
        ]);
        scrollToBottom();
    };

    const isAllClosed = useMemo(() => 
        paragraphs.length > 0 && paragraphs.every(p => !p.isOpen)
    , [paragraphs]);

    const allClosedLabel = useMemo(() => 
        intl.formatMessage({ id: isAllClosed ? "b0016" : "b0017" }),
    [intl, isAllClosed]);

    useEffect(() => {
        setParagraphs(prev => defaultParagraphs?.map((p, i) => ({ ...p, isOpen: prev[i]?.isOpen ?? true })) ?? []);
    }, [defaultParagraphs]);

    return (<>
        <TemplateConvertModal onSubmit={onConvertToTemplate} />

        <div className={classNames("flex flex-col", propsClassName)}>
            {paragraphs.length > 0 && (
                <div className="mb-4 flex items-center gap-1.5">
                    <Tooltip placement="top"
                             offset={4}
                             message={allClosedLabel}
                    >
                        <button type="button"
                                className="transition-transform duration-200"
                                data-testid="paragraph-editor-toggle-all"
                                aria-expanded={!isAllClosed}
                                aria-label={allClosedLabel}
                                onClick={onToggleAllOpen}
                        >
                            <ChevronArrowIcon className="size-4"
                                              direction={isAllClosed ? "down" : "up"}
                            />
                        </button>
                    </Tooltip>
                    <div className="text-black-500 text-[15px] font-[600] leading-none">
                        {intl.formatMessage({ id: "i04001" })}
                    </div>
                </div>  
            )}

            <SortableWrapper items={paragraphs}
                             className="mb-2 flex-1 min-h-0 flex flex-col gap-2 empty:mb-0"
                             onDragSortEnd={update}
            >{paragraphs.map((paragraph, index) => (
                <SortableItemWrapper key={`${index}-${paragraph.id}`}
                                     item={paragraph}
                                     dragTrigger="full"
                >
                    <ParagraphCard index={index}
                                   total={paragraphs.length}
                                   paragraph={paragraph}
                                   onToggleOpen={() => onToggleOpen(index)}
                                   onChange={(paragraph) => onChange(index, paragraph)}
                                   onDuplicate={() => onDuplicate(paragraph)}
                                   onMoveIndex={toIndex => onMove(index, toIndex)}
                                   onDelete={() => update(toSpliced(paragraphs, index, 1))}
                    />
                </SortableItemWrapper>
            ))}</SortableWrapper>

            <Button size="full"
                    variant="primary"
                    className="py-2"
                    onClick={onClickAddParagraph}
            >
                {intl.formatMessage({ id: "i04002" })}
            </Button>
        </div>
    </>);
};

export default ParagraphEditor;
